/**
 * Conference approvals — the BARRISTER's decision surface.
 *
 * The barrister only ever DECIDES: Approve a proposed slot, Decline, or Suggest an
 * alternative. They never schedule — the clerk coordinates the logistics. This card
 * shows only conferences awaiting the barrister's decision.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Gavel, CheckCircle, XCircle, CalendarPlus, CircleNotch } from '@phosphor-icons/react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getApiErrorMessage } from '@/lib/api';
import {
  fetchConferences, respondToConferenceProposal, PURPOSE_LABELS, FORMAT_LABELS, type Conference,
} from '@/lib/conferencesApi';
import { useAuthStore } from '@/features/auth/store';
import { useProposedSlotClashes } from '@/lib/useBarristerBusy';
import { ProposedSlotClashWarning } from '@/components/shared/ProposedSlotClashWarning';
import { ScrollLock } from '@/components/shared/ScrollLock';

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function ConferenceApprovals() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['conferences', 'approvals'],
    queryFn: () => fetchConferences({ limit: 100 }),
    refetchInterval: 120_000,
  });
  const pending = useMemo(
    () => (data?.conferences ?? []).filter((c) => c.status === 'awaiting_barrister'),
    [data],
  );

  const [suggestFor, setSuggestFor] = useState<Conference | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['conferences'] });

  const respondMut = useMutation({
    mutationFn: ({ id, ...p }: { id: string; decision: 'approve' | 'decline'; chosenDate?: string; note?: string }) =>
      respondToConferenceProposal(id, p),
    onSuccess: (_r, v) => { toast.success(v.decision === 'approve' ? 'Approved — the clerk will confirm' : 'Declined — the clerk will re-propose'); invalidate(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not submit your decision')),
  });

  if (pending.length === 0) return null;

  return (
    <Card className="border-blue-200 dark:border-blue-500/30">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Gavel className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <CardTitle>Conference approval required</CardTitle>
          <Badge variant="info">{pending.length}</Badge>
        </div>
        <CardDescription>Your clerk proposed these. Approve a time, decline, or suggest another.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending.map((c) => (
          <div key={c.id} className="rounded-lg border border-border px-3 py-3">
            <div className="text-sm font-medium text-foreground">{c.case.caseNumber} · {c.case.caseName}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {PURPOSE_LABELS[c.purpose]} · {FORMAT_LABELS[c.format]} · {c.durationMinutes} min
              {c.locationDetails ? ` · ${c.locationDetails}` : ''}
            </div>
            <div className="mt-2 space-y-1.5">
              {(c.proposedDates ?? []).map((d) => (
                <div key={d} className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                  <span className="text-sm text-foreground">{fmt(d)}</span>
                  <Button
                    variant="primary" size="sm"
                    disabled={respondMut.isPending}
                    leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                    onClick={() => respondMut.mutate({ id: c.id, decision: 'approve', chosenDate: d })}
                  >
                    Approve this
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                disabled={respondMut.isPending}
                leftIcon={<XCircle className="h-3.5 w-3.5" />}
                onClick={() => {
                  const note = window.prompt('Decline — briefly, why are you unavailable? (the clerk will re-propose)');
                  if (note && note.trim()) respondMut.mutate({ id: c.id, decision: 'decline', note: note.trim() });
                }}
              >
                Decline all
              </Button>
              <Button
                variant="outline" size="sm"
                leftIcon={<CalendarPlus className="h-3.5 w-3.5" />}
                onClick={() => setSuggestFor(c)}
              >
                Suggest another time
              </Button>
            </div>
          </div>
        ))}
      </CardContent>

      {suggestFor && (
        <SuggestModal conference={suggestFor} onClose={() => setSuggestFor(null)} onDone={() => { setSuggestFor(null); invalidate(); }} />
      )}
    </Card>
  );
}

function SuggestModal({ conference, onClose, onDone }: { conference: Conference; onClose: () => void; onDone: () => void }) {
  const [dates, setDates] = useState<string[]>(['', '']);
  const [note, setNote] = useState('');
  // Warn (never block) if a time you suggest clashes your own diary.
  const myUserId = useAuthStore((s) => s.user?.id ?? null);
  const isoDates = dates.filter(Boolean).map((d) => new Date(d).toISOString());
  const clashes = useProposedSlotClashes(myUserId, isoDates, conference.durationMinutes);
  const mut = useMutation({
    mutationFn: () => respondToConferenceProposal(conference.id, {
      decision: 'suggest',
      suggestedDates: dates.filter(Boolean).map((d) => new Date(d).toISOString()),
      note: note || undefined,
    }),
    onSuccess: () => { toast.success('Suggested — the clerk will coordinate'); onDone(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not suggest')),
  });
  const valid = dates.some(Boolean);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Suggest alternative times — {conference.case.caseNumber}</h3>
        <div className="space-y-3">
          {dates.map((d, i) => (
            <input key={i} type="datetime-local" value={d} onChange={(e) => setDates((p) => p.map((v, j) => (j === i ? e.target.value : v)))} className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm" />
          ))}
          <ProposedSlotClashWarning clashes={clashes} barristerLabel="You" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note for the clerk" className="w-full rounded-md border border-border bg-card p-2 text-sm" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!valid || mut.isPending} leftIcon={mut.isPending ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : undefined} onClick={() => mut.mutate()}>Send to clerk</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
